import React from 'react';
import { ChartConfig, AdvancedConfig, ScientificChartConfig, ThreeDBarOptions } from '../../types';
import { Activity, Layers, Move, Map as MapIcon, Grid, ArrowRight, Layout, Box, Maximize } from 'lucide-react';
import { DEFAULT_3D_BAR_CONFIG } from '../../src/charts/ThreeDBarChart/config/threeDBarDefaults';
import { ForceGraphConfig } from '../../src/charts/ForceGraph/config/forceGraphTypes';
import { ThreeDBarMode, YScaleType } from '../../src/charts/ThreeDBarChart/config/threeDBarTypes';

interface AdvancedDataMappingProps {
    config: ChartConfig;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onAdvancedChange: (newAdvanced: AdvancedConfig) => void;
    allCols: string[];
    numericCols: string[];
}

export const AdvancedDataMapping: React.FC<AdvancedDataMappingProps> = ({
    config,
    onChange,
    onAdvancedChange,
    allCols,
    numericCols
}) => {
    const { type } = config;
    const advanced = config.advanced || {};

    const updateAdvanced = (section: keyof AdvancedConfig, key: string, value: any) => {
        onAdvancedChange({
            ...advanced,
            [section]: {
                ...advanced[section] as any,
                [key]: value
            }
        });
    };

    const handleScientificChange = (key: keyof ScientificChartConfig, value: any) => {
        const currentSci = advanced.scientific || {};
        onAdvancedChange({
            ...advanced,
            scientific: { ...currentSci, [key]: value } as ScientificChartConfig
        });
    };

    const handleNestedScientificChange = (parent: keyof ScientificChartConfig, key: string, value: any) => {
        const currentSci = advanced.scientific || {};
        const parentObj = (currentSci as any)[parent] || {};
        onAdvancedChange({
            ...advanced,
            scientific: {
                ...currentSci,
                [parent]: { ...parentObj, [key]: value }
            } as ScientificChartConfig
        });
    };

    // Force Directed Graph
    if (type === 'force-directed') {
        const forceConfig = (advanced.forceGraph || {}) as ForceGraphConfig;
        return (
            <div className="space-y-6">
                <div className="space-y-3">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5" /> Data Mapping
                    </h4>
                    <div className="space-y-2">
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-400">Source Node Field</label>
                            <select
                                name="sourceField"
                                value={config.sourceField || ''}
                                onChange={onChange}
                                className="w-full text-sm bg-obsidian-800 border border-white/20 rounded-lg px-2 py-1 text-gray-200"
                            >
                                <option value="">Select Field</option>
                                {allCols.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-400">Target Node Field</label>
                            <select
                                name="targetField"
                                value={config.targetField || ''}
                                onChange={onChange}
                                className="w-full text-sm bg-obsidian-800 border border-white/20 rounded-lg px-2 py-1 text-gray-200"
                            >
                                <option value="">Select Field</option>
                                {allCols.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="h-px bg-white/10" />

                <div className="space-y-3">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Layout className="w-3.5 h-3.5" /> Layout
                    </h4>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-400">Mode</label>
                        <select
                            value={forceConfig.layoutMode || 'force'}
                            onChange={(e) => updateAdvanced('forceGraph', 'layoutMode', e.target.value)}
                            className="w-full text-sm bg-obsidian-800 border border-white/20 rounded-lg px-2 py-1 text-gray-200"
                        >
                            <option value="force">Force Directed</option>
                            <option value="radial">Radial</option>
                            <option value="hierarchy">Hierarchy</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-400">Repulsion: {forceConfig.repulsionStrength || 100}</label>
                        <input
                            type="range"
                            min="0"
                            max="1000"
                            value={forceConfig.repulsionStrength || 100}
                            onChange={(e) => updateAdvanced('forceGraph', 'repulsionStrength', Number(e.target.value))}
                            className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-400">Link Distance: {forceConfig.linkDistance || 50}</label>
                        <input
                            type="range"
                            min="10"
                            max="300"
                            value={forceConfig.linkDistance || 50}
                            onChange={(e) => updateAdvanced('forceGraph', 'linkDistance', Number(e.target.value))}
                            className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Vector Chart
    if (type === 'vector') {
        const vec = advanced.vector || { uField: null, vField: null };
        return (
            <div className="space-y-4">
                <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                    <ArrowRight className="w-3.5 h-3.5" /> Vector Fields
                </h4>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">X Position</label>
                        <select
                            name="xField"
                            value={config.xField || ''}
                            onChange={onChange}
                            className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">Y Position</label>
                        <select
                            name="yField"
                            value={config.yField || ''}
                            onChange={onChange}
                            className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">U (X-Dir)</label>
                        <select
                            value={vec.uField || ''}
                            onChange={(e) => updateAdvanced('vector', 'uField', e.target.value)}
                            className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">V (Y-Dir)</label>
                        <select
                            value={vec.vField || ''}
                            onChange={(e) => updateAdvanced('vector', 'vField', e.target.value)}
                            className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>
            </div>
        );
    }

    // 3D Scatter
    if (type === '3d') {
        const td = advanced.threeD || { xField: null, yField: null, zField: null };
        return (
            <div className="space-y-4">
                <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                    <Move className="w-3.5 h-3.5" /> 3D Mapping
                </h4>
                <div className="space-y-2">
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">X Field</label>
                        <select
                            value={td.xField || ''}
                            onChange={(e) => updateAdvanced('threeD', 'xField', e.target.value)}
                            className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">Y Field</label>
                        <select
                            value={td.yField || ''}
                            onChange={(e) => updateAdvanced('threeD', 'yField', e.target.value)}
                            className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">Z Field</label>
                        <select
                            value={td.zField || ''}
                            onChange={(e) => updateAdvanced('threeD', 'zField', e.target.value)}
                            className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>
            </div>
        );
    }

    // Globe


    // Map
    if (type === 'map') {
        const map = advanced.map || { latField: null, lonField: null };
        return (
            <div className="space-y-4">
                <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                    <MapIcon className="w-3.5 h-3.5" /> Map Coordinates
                </h4>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">Latitude</label>
                        <select
                            value={map.latField || ''}
                            onChange={(e) => updateAdvanced('map', 'latField', e.target.value)}
                            className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">Longitude</label>
                        <select
                            value={map.lonField || ''}
                            onChange={(e) => updateAdvanced('map', 'lonField', e.target.value)}
                            className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">Region Key (Choropleth)</label>
                        <select
                            value={map.regionKeyField || ''}
                            onChange={(e) => updateAdvanced('map', 'regionKeyField', e.target.value)}
                            className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {allCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">Value (Size/Color)</label>
                        <select
                            value={map.valueField || ''}
                            onChange={(e) => updateAdvanced('map', 'valueField', e.target.value)}
                            className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>
            </div>
        );
    }

    // 3D Bar
    if (type === '3d-bar') {
        const threeDBar = { ...DEFAULT_3D_BAR_CONFIG, ...(advanced.threeDBar || {}) } as ThreeDBarOptions;
        return (
            <div className="space-y-6">
                <div className="space-y-3">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Grid className="w-3.5 h-3.5" /> Data Mapping
                    </h4>
                    <div className="space-y-2">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-400">Category Field (X-Axis)</label>
                            <select
                                name="xField"
                                value={config.xField || ''}
                                onChange={onChange}
                                className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            >
                                <option value="">Select Field</option>
                                {allCols.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-400">Value Field (Y-Axis)</label>
                            <select
                                name="yField"
                                value={config.yField || ''}
                                onChange={onChange}
                                className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            >
                                <option value="">Select Field</option>
                                {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-400">Depth Field (Z-Axis)</label>
                            <select
                                value={threeDBar.zField || ''}
                                onChange={(e) => {
                                    onAdvancedChange({
                                        ...advanced,
                                        threeDBar: { ...threeDBar, zField: e.target.value }
                                    });
                                }}
                                className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            >
                                <option value="">None</option>
                                {allCols.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="h-px bg-white/10" />

                <div className="space-y-3">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Box className="w-3.5 h-3.5" /> Layout
                    </h4>

                    <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-400">Mode</label>
                            <select
                                value={threeDBar.mode}
                                onChange={(e) => updateAdvanced('threeDBar', 'mode', e.target.value)}
                                className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            >
                                <option value="grouped">Grouped</option>
                                <option value="stacked">Stacked</option>
                                <option value="single">Single</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-400">Y-Scale</label>
                            <select
                                value={threeDBar.yScaleType}
                                onChange={(e) => updateAdvanced('threeDBar', 'yScaleType', e.target.value)}
                                className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            >
                                <option value="linear">Linear</option>
                                <option value="log">Logarithmic</option>
                            </select>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs text-gray-400 block">Bar Gap: {threeDBar.barGap}</label>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={threeDBar.barGap}
                            onChange={(e) => updateAdvanced('threeDBar', 'barGap', parseFloat(e.target.value))}
                            className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Scientific
    if (type === 'scientific') {
        const scientificConfig = (advanced.scientific || {}) as Partial<ScientificChartConfig>;
        return (
            <div className="space-y-6">
                <div className="space-y-4">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Layers className="w-3.5 h-3.5" /> Data Mapping
                    </h4>

                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">X-Axis Field</label>
                        <select
                            name="xField"
                            value={config.xField || ''}
                            onChange={onChange}
                            className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200 focus:ring-2 focus:ring-gold-500/50"
                        >
                            <option value="">Select Field</option>
                            {allCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">Y-Axis Field</label>
                        <select
                            name="yField"
                            value={config.yField || ''}
                            onChange={onChange}
                            className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200 focus:ring-2 focus:ring-gold-500/50"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    {/* Z-Axis Field (3D) */}
                    {scientificConfig.plotType?.startsWith('3d-') && (
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-400">Z-Axis Field</label>
                            <select
                                value={scientificConfig.threeD?.zField || ''}
                                onChange={(e) => handleNestedScientificChange('threeD', 'zField', e.target.value)}
                                className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200 focus:ring-2 focus:ring-gold-500/50"
                            >
                                <option value="">Select Field</option>
                                {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Vector Fields */}
                    {scientificConfig.plotType === 'vector' && (
                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-gray-400">U (X-Vector)</label>
                                <select
                                    value={scientificConfig.vector?.uField || ''}
                                    onChange={(e) => handleNestedScientificChange('vector', 'uField', e.target.value)}
                                    className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                                >
                                    <option value="">Select Field</option>
                                    {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-gray-400">V (Y-Vector)</label>
                                <select
                                    value={scientificConfig.vector?.vField || ''}
                                    onChange={(e) => handleNestedScientificChange('vector', 'vField', e.target.value)}
                                    className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                                >
                                    <option value="">Select Field</option>
                                    {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                <div className="h-px bg-white/10" />

                {/* Plot Type Selection */}
                <div className="space-y-2">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5" /> Plot Type
                    </h4>
                    <select
                        value={scientificConfig.plotType || 'line'}
                        onChange={(e) => handleScientificChange('plotType', e.target.value)}
                        className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200 focus:ring-2 focus:ring-gold-500/50"
                    >
                        <optgroup label="2D Plots">
                            <option value="line">Scientific Line Plot</option>
                            <option value="scatter">Scientific Scatter</option>
                            <option value="vector">Vector Field (Quiver)</option>
                        </optgroup>
                        <optgroup label="3D Plots">
                            <option value="3d-scatter">3D Scatter</option>
                            <option value="3d-surface">3D Surface</option>
                            <option value="3d-wireframe">3D Wireframe</option>
                        </optgroup>
                    </select>
                </div>

                {/* Analysis Tools */}
                <div className="space-y-3 border-t border-gold-500/10 pt-4">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5" /> Analysis Tools
                    </h4>
                    <div className="space-y-2">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-400">Trendline Type</label>
                            <select
                                value={scientificConfig.analysis?.trendline || 'none'}
                                onChange={(e) => handleNestedScientificChange('analysis', 'trendline', e.target.value)}
                                className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            >
                                <option value="none">None</option>
                                <option value="linear">Linear</option>
                                <option value="poly2">Polynomial (Degree 2)</option>
                                <option value="poly3">Polynomial (Degree 3)</option>
                                <option value="poly">Polynomial (Custom)</option>
                                <option value="exp">Exponential</option>
                            </select>
                        </div>

                        {scientificConfig.analysis?.trendline === 'poly' && (
                            <div>
                                <label className="text-xs text-gray-400 block mb-1">Degree</label>
                                <input
                                    type="number"
                                    min="2"
                                    max="10"
                                    value={scientificConfig.analysis?.trendlineDegree || 2}
                                    onChange={(e) => handleNestedScientificChange('analysis', 'trendlineDegree', parseInt(e.target.value))}
                                    className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                                />
                            </div>
                        )}

                        <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={scientificConfig.analysis?.showEquation || false}
                                    onChange={(e) => handleNestedScientificChange('analysis', 'showEquation', e.target.checked)}
                                    className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                                />
                                Show Equation
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={scientificConfig.analysis?.confidenceInterval || false}
                                    onChange={(e) => handleNestedScientificChange('analysis', 'confidenceInterval', e.target.checked)}
                                    className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                                />
                                95% CI
                            </label>
                        </div>

                        {scientificConfig.analysis?.confidenceInterval && (
                            <div>
                                <label className="text-xs text-gray-400 block mb-1">CI Opacity: {scientificConfig.analysis?.confidenceOpacity || 0.2}</label>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={scientificConfig.analysis?.confidenceOpacity || 0.2}
                                    onChange={(e) => handleNestedScientificChange('analysis', 'confidenceOpacity', parseFloat(e.target.value))}
                                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Error Bars */}
                <div className="space-y-3 border-t border-gold-500/10 pt-4">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Layers className="w-3.5 h-3.5" /> Error Bars
                    </h4>

                    <div className="space-y-2">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-400">Error Mode</label>
                            <select
                                value={scientificConfig.errorBars?.mode || 'none'}
                                onChange={(e) => {
                                    handleNestedScientificChange('errorBars', 'mode', e.target.value);
                                    handleNestedScientificChange('errorBars', 'show', e.target.value !== 'none');
                                }}
                                className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            >
                                <option value="none">None</option>
                                <option value="constant">Constant</option>
                                <option value="field">From Field</option>
                            </select>
                        </div>

                        {scientificConfig.errorBars?.mode === 'constant' && (
                            <div>
                                <label className="text-xs text-gray-400 block mb-1">± Error Value</label>
                                <input
                                    type="number"
                                    value={scientificConfig.errorBars?.constantValue || 1}
                                    onChange={(e) => handleNestedScientificChange('errorBars', 'constantValue', parseFloat(e.target.value))}
                                    className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                                />
                            </div>
                        )}

                        {scientificConfig.errorBars?.mode === 'field' && (
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-gray-400">Error Field</label>
                                <select
                                    value={scientificConfig.errorBars?.yErrorField || ''}
                                    onChange={(e) => handleNestedScientificChange('errorBars', 'yErrorField', e.target.value)}
                                    className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                                >
                                    <option value="">Select Field</option>
                                    {numericCols.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Transformations */}
                <div className="space-y-3 border-t border-gold-500/10 pt-4">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5" /> Transformations
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-400">X Transform</label>
                            <select
                                value={scientificConfig.transformations?.x || 'none'}
                                onChange={(e) => handleNestedScientificChange('transformations', 'x', e.target.value)}
                                className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            >
                                <option value="none">None</option>
                                <option value="log10">log10(x)</option>
                                <option value="ln">ln(x)</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-400">Y Transform</label>
                            <select
                                value={scientificConfig.transformations?.y || 'none'}
                                onChange={(e) => handleNestedScientificChange('transformations', 'y', e.target.value)}
                                className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            >
                                <option value="none">None</option>
                                <option value="log10">log10(y)</option>
                                <option value="ln">ln(y)</option>
                                <option value="sqrt">sqrt(y)</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Axis Options */}
                <div className="space-y-2 border-t border-gold-500/10 pt-4">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Maximize className="w-3.5 h-3.5" /> Axis Control
                    </h4>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                            <input
                                type="checkbox"
                                checked={scientificConfig.axisOptions?.logScaleX || false}
                                onChange={(e) => handleNestedScientificChange('axisOptions', 'logScaleX', e.target.checked)}
                                className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                            />
                            Log Scale X
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                            <input
                                type="checkbox"
                                checked={scientificConfig.axisOptions?.logScaleY || false}
                                onChange={(e) => handleNestedScientificChange('axisOptions', 'logScaleY', e.target.checked)}
                                className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                            />
                            Log Scale Y
                        </label>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};
